import { useEffect, useState } from 'react';
import type {
  ApiClient, SanitaerBerechnungsbereich, SanitaerBerechnungszeile,
} from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, PropertyGrid, formatiereZahl,
  LoadingState, StatusBadge, Vorbehalte, WertEinheit, type ExportZiel, type Spalte, type Vorbehalt,
} from '../muster';
import { PageHeader } from '../rahmen/PageHeader';
import { Rechenstand, sanitaerFachvorbehalt, zeitpunkt } from './sanitaer/kopf';
import './sanitaer/sanitaer.css';

/**
 * Die Ergebnisse einer Sanitärberechnung.
 *
 * --- Warum eine Ansicht für fünf Bereiche -------------------------------------
 *
 * Trinkwasser, Warmwasser, Schmutzwasser, Fettabwasser und Regenwasser tragen
 * verschiedene Größen — der Strang Fließdruck und Geschwindigkeit, das
 * Netzteil Füllungsgrad und Gefälle, der Fettabscheider Nenngröße und
 * Schlammfang.
 *
 * Fünf Ansichten wären fünfmal dieselbe Tabelle mit anderen Spaltennamen, und
 * die sechste — bei der nächsten Kennzahl — wäre wieder eine neue Datei.
 *
 * Deshalb baut die Tabelle ihre SPALTEN AUS DEN DATEN. Was in `werte` steht,
 * wird zur Spalte; die Reihenfolge kommt aus der ersten Zeile der Gattung.
 *
 * --- Warum der ungünstigste Strang oben steht und trotzdem alle da sind -------
 *
 * Ausdrückliche Vorgabe: ungünstigster Strang UND alle anderen vollständig.
 *
 * Eine Auslegung, die nur den schlechtesten Fall zeigt, lässt den Prüferblick
 * auf die 350 anderen nicht zu — und eine, die alle 350 gleichrangig auflistet,
 * verbirgt den einen, an dem die Anlage hängt. Deshalb beides: hervorgehoben
 * und trotzdem vollständig.
 *
 * --- Warum der Rechenweg aufklappbar ist --------------------------------------
 *
 * Bei der Heizung hat sich gezeigt, dass eine Zahl ohne ihren Weg nicht geprüft
 * wird — sie wird geglaubt oder verworfen, und beides ist keine Prüfung.
 *
 * Immer sichtbar wäre er aber unbrauchbar: Fünf Schritte mal 350 Netzteile sind
 * 1.750 Zeilen Text zwischen den Zahlen.
 *
 * --- Kopf nach TX S1.2 (Paket TS9a) -----------------------------------------
 *
 * `PageHeader` mit dem Rechenstand des Laufs und der Exportleiste (PDF Bericht,
 * Excel des Laufs aus TS11, PDF Schema über das Register); darunter `Vorbehalte`:
 * die offene Fachfrage des Bereichs (SAN-05 bzw. AUA-22, derselbe Satz wie in
 * der Excel-Mappe), der Zwischenstand bei doppelter Verknüpfung (PB44) und
 * „veraltet“ (S-21). Bis TS9a standen hier eigene Knöpfe und zwei Kästen.
 */

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
  bereich: string;
  /** Nur diese Gattungen zeigen. Leer heißt alle. */
  nurGattungen?: string[];
  /** Diese Gattungen NICHT zeigen — die Gegenprobe zu nurGattungen. */
  ohneGattungen?: string[];
  titel: string;
  einleitung?: React.ReactNode;
  /**
   * PDF Bericht und PDF Schema gesperrt, mit diesem Grund (TS9b2: Überflutung, solange die Berichtsart SAN-UEB bei
   * TX nicht angemeldet ist). Excel bleibt davon unberührt.
   */
  pdfGesperrt?: string;
}

const GATTUNGSNAMEN: Record<string, string> = {
  druckerhoehung: 'Druckerhöhung — Gesamtnetz',
  druckzone: 'Druckerhöhung je Druckzone',
  strang: 'Stränge',
  netzteil: 'Netzteile',
  station: 'Frischwasserstationen',
  durchlauferhitzer: 'Durchlauferhitzer',
  hebeanlage: 'Hebeanlagen',
  vorbehaelter: 'Vorbehälteranlagen',
  fettabscheider: 'Fettabscheider',
  dachablauf: 'Dachabläufe',
  dachdeckung: 'Deckung der Dachfläche (Gegenprobe)',
  zirkulation: 'Zirkulation nach DVGW W 553',
  w551: 'Anforderungen des DVGW W 551',
};

/** Die Reihenfolge, in der Gattungen erscheinen. */
const RANG: Record<string, number> = {
  // Die drei Anforderungen des W 551 stehen ganz vorne: Sie beantworten, ob
  // die Norm eingehalten ist, an der der Berichtskopf hängt (Frage 24) — und
  // nicht, wie groß ein Gerät sein muss.
  w551: -1,
  druckzone: 0, druckerhoehung: 1, strang: 2, netzteil: 3, station: 4,
  zirkulation: 4.5, durchlauferhitzer: 5, hebeanlage: 6, vorbehaelter: 7,
  fettabscheider: 8, dachdeckung: 8.5,
};

export function SanitaerBerechnung({
  api, projektId, bereich, nurGattungen, ohneGattungen, titel, einleitung, pdfGesperrt,
}: Eigenschaften) {
  const [daten, setDaten] = useState<SanitaerBerechnungsbereich | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    setLaedt(true);
    setFehler(null);

    api.sanitaerBerechnungBereich(projektId, bereich)
      .then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setLaedt(false));
  }, [api, projektId, bereich]);

  // Ohne Lauf gibt es nichts auszugeben - die Knöpfe bleiben, gesperrt mit Grund (S1.9).
  const ohneLauf = !daten ? { grund: 'Für diesen Bereich liegt noch keine Berechnung vor.' } : undefined;
  const pdfSperre = pdfGesperrt ? { grund: pdfGesperrt } : ohneLauf;
  const ziele: ExportZiel[] = [
    { art: 'pdf', beschriftung: 'PDF Bericht', laden: () => api.sanitaerBerichtPdf(projektId, bereich), gesperrt: pdfSperre },
    { art: 'excel', beschriftung: 'Excel', laden: () => api.sanitaerBerechnungExcel(projektId, bereich), gesperrt: ohneLauf },
    {
      // TS10 (AG-F2): das Schema kommt über das Register (TWS/RWS/SWS) - kein Sichtstand mehr.
      art: 'pdf', beschriftung: 'PDF Schema', laden: () => api.sanitaerSchemaPdf(projektId, bereich),
      gesperrt: pdfSperre,
    },
  ];
  const kopf = (
    <PageHeader
      titel={titel}
      meta={laedt ? undefined : <Rechenstand lauf={daten?.lauf} veraltet={daten?.lauf.standVeraltet} />}
      aktionen={<ExportLeiste ziele={ziele} />}
    />
  );

  if (laedt) return <>{kopf}<LoadingState text={`${titel} wird abgerufen …`} zeilen={8} spalten={6} /></>;

  if (fehler) {
    return (
      <>
        {kopf}
        <EmptyState
          titel={`Für „${titel}“ wurde noch keine Berechnung durchgeführt.`}
          erklaerung={'Liegt für diesen Bereich noch keine Berechnung vor, entsteht sie '
            + 'über „Alle Bereiche rechnen“ auf der Bestandsseite.'}
          aktion={<ErrorState klein meldung={fehler} />}
        />
      </>
    );
  }

  if (!daten) return null;

  const vorbehalte: Vorbehalt[] = [];
  const frage = sanitaerFachvorbehalt(bereich);
  if (frage) vorbehalte.push(frage);
  // PB44: Steht die Grundlage doppelt da, ist jede Summe auf dieser Seite zu groß - gemessen am 21.09.2026 um 46,3 %.
  if (daten.lauf.bemessungGesperrt) {
    vorbehalte.push({
      art: 'sonstiges',
      text: `Zwischenstand, keine Bemessung (doppelte Verknüpfung): ${daten.lauf.sperrgrund ?? ''} Die Zahlen sind `
        + 'nicht entdoppelt - als Zwischenstand brauchbar, als Nachweis nach Norm nicht; der Fachplaner entscheidet, '
        + 'welche Verknüpfung gilt.',
    });
  }
  // S-21: Der Befund war nicht die Abweichung, sondern dass ein Stand als aktuell auswies, den es nicht mehr gibt.
  if (daten.lauf.standVeraltet) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Gerechnet am ${zeitpunkt(daten.lauf.gerechnetAm)}, Sanitäraufnahme vom ${zeitpunkt(daten.lauf.modellstand)}`
        + (typeof daten.lauf.veraltetTage === 'number'
          ? ` - ${formatiereZahl(Math.round(daten.lauf.veraltetTage * 10) / 10)} Tage dazwischen` : '')
        + '. Jede Zahl auf dieser Seite gilt für einen Modellstand, den es nicht mehr gibt. Über „Alle Bereiche '
        + 'rechnen“ auf der Bestandsseite entsteht der neue Stand.',
    });
  }

  const sichtbar = daten.zeilen.filter((z) =>
    (!nurGattungen || nurGattungen.includes(z.gattung))
    && (!ohneGattungen || !ohneGattungen.includes(z.gattung)));

  const gattungen = [...new Set(sichtbar.map((z) => z.gattung))]
    .sort((a, b) => (RANG[a] ?? 9) - (RANG[b] ?? 9));

  return (
    <>
      {kopf}
      <Vorbehalte eintraege={vorbehalte} />

      <ContentSection
        titel="Überblick"
        beschreibung={einleitung}
      >
        <KpiReihe>
          <KpiCard titel="Zeilen" wert={sichtbar.length} />
          <KpiCard
            titel="beanstandet"
            wert={sichtbar.filter((z) => z.bewertung === 'beanstandet').length}
            zustand={sichtbar.some((z) => z.bewertung === 'beanstandet') ? 'warnung' : 'gut'}
            zustandText={sichtbar.some((z) => z.bewertung === 'beanstandet')
              ? 'prüfen' : 'keine Beanstandung'}
          />
          <KpiCard
            titel="Hinweise"
            wert={sichtbar.filter((z) => z.bewertung === 'hinweis').length}
          />
        </KpiReihe>
      </ContentSection>

      {/* ── 1. WORAUF gerechnet wird ──────────────────────────────────
          Norm und Eingangsgrößen zuerst. Ein liNear-Berechnungsblatt
          beginnt damit, und aus gutem Grund: Wer die Zahlen beurteilen
          soll, muss zuerst wissen, worauf sie stehen. */}
      {daten.lauf.annahmen && <Annahmenkarte annahmen={daten.lauf.annahmen} />}

      {/* ── 2. WAS herauskommt ────────────────────────────────────────── */}
      {daten.lauf.kennzahlen && (
        <Kennzahlenkarte werte={daten.lauf.kennzahlen} />
      )}

      {daten.lauf.hinweise && daten.lauf.hinweise.length > 0 && (
        <ContentSection titel="Hinweise zum Lauf">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {daten.lauf.hinweise.map((h, i) => (
              <li key={i}>
                {h.startsWith('ACHTUNG')
                  ? <StatusBadge zustand="warnung" text={h} klein />
                  : h}
              </li>
            ))}
          </ul>
        </ContentSection>
      )}

      {gattungen.length === 0 ? (
        <EmptyState
          titel="Für diesen Abschnitt hat die Berechnung keine Zeilen erzeugt."
          erklaerung={'Der Lauf ist gelaufen, hat aber für diese Gattungen nichts '
            + 'gefunden — meist fehlt der Bestand, auf den er sich stützt.'}
        />
      ) : gattungen.map((g) => (
        <Gattungstabelle
          key={g}
          gattung={g}
          zeilen={sichtbar.filter((z) => z.gattung === g)}
        />
      ))}
    </>
  );
}

/**
 * Die Kennzahlen des Laufs.
 *
 * Aus den Daten und nicht aus einer festen Liste — sie sind je Bereich andere,
 * und eine Liste je Bereich wäre fünf Listen, die bei der nächsten Kennzahl
 * auseinanderlaufen.
 */
function Kennzahlenkarte({ werte }: { werte: Record<string, unknown> }) {
  const eintraege = Object.entries(werte)
    .filter(([, v]) => v !== null && typeof v !== 'object');

  if (eintraege.length === 0) return null;

  return (
    <ContentSection titel="Kennzahlen">
      <KpiReihe>
        {eintraege.map(([k, v]) => (
          <KpiCard key={k} titel={lesbar(k)} wert={formatiere(v)} />
        ))}
      </KpiReihe>
    </ContentSection>
  );
}

/**
 * Die Annahmen mit ihrem Prüfstand.
 *
 * Die UNGEPRÜFTEN zuerst: Sie sind die Stellen, an denen die Rechnung auf
 * einer Entscheidung steht, die niemand getroffen hat — und wer das Ergebnis
 * prüft, soll sie nicht suchen müssen.
 */
function Annahmenkarte({ annahmen }: {
  annahmen: NonNullable<SanitaerBerechnungsbereich['lauf']['annahmen']>;
}) {
  const [offen, setOffen] = useState(false);

  const ungeprueft = annahmen.filter((a) => !a.geprueft);

  const spalten: Spalte<any>[] = [
    {
      schluessel: 'annahme', titel: 'Annahme', immerSichtbar: true,
      wert: (a) => lesbar(a.schluessel),
    },
    {
      schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
      wert: (a) => a.wert,
      inhalt: (a) => (a.wert === null
        ? <StatusBadge zustand="warnung" text="nicht gepflegt" klein />
        : <WertEinheit wert={a.wert} einheit={a.einheit ?? undefined} rechts />),
    },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (a) => a.herkunft },
    {
      schluessel: 'pruefstand', titel: 'Prüfstand', art: 'status', breite: '160px',
      wert: (a) => (a.geprueft ? 'geprüft' : 'ungeprüft'),
      inhalt: (a) => (a.geprueft
        ? <StatusBadge zustand="gut" text="geprüft" klein />
        : <StatusBadge zustand="warnung" text="ungeprüft" klein />),
    },
  ];

  return (
    <ContentSection
      titel="Annahmen"
      meta={ungeprueft.length > 0
        ? <StatusBadge zustand="warnung" text={`${ungeprueft.length} ungeprüft`} klein />
        : <StatusBadge zustand="gut" text="alle geprüft" klein />}
      aktionen={(
        <button className="m-knopf" onClick={() => setOffen(!offen)}>
          {offen ? 'nur die ungeprüften' : 'alle ansehen'}
        </button>
      )}
      nackt
    >
      <DataTable
        name="Annahmen"
        spalten={spalten}
        zeilen={(offen ? annahmen : ungeprueft)
          .slice()
          .sort((a, b) => Number(a.geprueft) - Number(b.geprueft))
          .map((a, i) => ({ ...a, _nr: i }))}
        schluessel={(a: any) => String(a.schluessel ?? `annahme-${a._nr}`)}
        ausfuhr
        ausfuhrDateiname="sanitaer-annahmen"
        dicht
        leer={(
          <EmptyState
            titel={offen ? 'Keine Annahme hinterlegt.' : 'Jede Annahme ist geprüft.'}
            erklaerung={offen
              ? 'Ohne Annahmen steht die Rechnung ausschließlich auf Norm- und Modellwerten.'
              : 'Das ist die gute Nachricht — die Rechnung steht auf keiner ungeprüften Entscheidung.'}
          />
        )}
      />
    </ContentSection>
  );
}

/**
 * Die Tabelle einer Gattung.
 *
 * Die Spalten kommen aus der ERSTEN Zeile: Innerhalb einer Gattung stammen
 * alle Werte aus demselben Datensatz, also tragen sie dieselben Schlüssel.
 * Eine Zeile mit weniger Feldern bekommt leere Zellen — das ist richtig und
 * nicht kaputt.
 *
 * Listen und Objekte werden übersprungen: Sie passen nicht in eine Zelle, und
 * in einer stünde dann „[object Object]“.
 */
function Gattungstabelle({ gattung, zeilen }: {
  gattung: string; zeilen: SanitaerBerechnungszeile[];
}) {
  const [offen, setOffen] = useState<string | null>(null);

  const sortiert = zeilen.slice().sort((a, b) => {
    if (a.istMassgebend !== b.istMassgebend) return a.istMassgebend ? -1 : 1;
    const rang = (z: SanitaerBerechnungszeile) =>
      z.bewertung === 'beanstandet' ? 0 : z.bewertung === 'hinweis' ? 1 : 2;
    if (rang(a) !== rang(b)) return rang(a) - rang(b);
    return a.ordnung - b.ordnung;
  });

  // Die Spalten kommen aus der ERSTEN Zeile: Innerhalb einer Gattung stammen
  // alle Werte aus demselben Datensatz, also tragen sie dieselben Schlüssel.
  // Listen und Objekte werden übersprungen — in einer Zelle stünde sonst
  // „[object Object]".
  const werteschluessel = Object.entries(sortiert[0]?.werte ?? {})
    .filter(([, v]) => v === null || typeof v !== 'object')
    .map(([k]) => k);

  // Die Beschriftung trägt die Einheit in eckigen Klammern; sie wird gelöst
  // und als Spalteneinheit gesetzt (Abschnitt 15, Paket U6b).
  const spalten: Spalte<SanitaerBerechnungszeile>[] = [
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', breite: '160px',
      wert: (z) => z.bewertung,
      inhalt: (z) => (z.bewertung === 'beanstandet'
        ? <StatusBadge zustand="warnung" text="beanstandet" klein />
        : z.bewertung === 'hinweis'
          ? <StatusBadge zustand="info" text="Hinweis" klein />
          : <StatusBadge zustand="gut" text="in Ordnung" klein />),
    },
    {
      schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true,
      wert: (z) => z.bezeichnung,
      inhalt: (z) => (
        <>
          {z.istMassgebend && (
            <StatusBadge zustand="info" text="maßgebend" klein
                         titel="der maßgebende Fall" />
          )}
          {' '}{z.bezeichnung}
        </>
      ),
    },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
    ...werteschluessel.map((k) => {
      const beschriftung = lesbar(k);
      const treffer = /^(.*?)\s*\[(.+)\]$/.exec(beschriftung);

      return {
        schluessel: `w_${k}`,
        titel: treffer ? treffer[1] : beschriftung,
        einheit: treffer ? treffer[2] : undefined,
        ausrichtung: 'rechts' as const,
        wert: (z: SanitaerBerechnungszeile) => z.werte?.[k],
        inhalt: (z: SanitaerBerechnungszeile) => <>{formatiere(z.werte?.[k])}</>,
      };
    }),
    {
      schluessel: 'befund', titel: 'Befund', wert: (z) => z.befund,
      inhalt: (z) => (
        <span className="schwach" style={{ fontSize: 12 }}>{z.befund ?? ''}</span>
      ),
    },
  ];

  const gewaehlt = sortiert.find((z) => z.id === offen) ?? null;

  return (
    <ContentSection
      titel={GATTUNGSNAMEN[gattung] ?? gattung}
      meta={`${zeilen.length} Zeilen`}
      beschreibung="Der maßgebende und die beanstandeten stehen oben."
      nackt
    >
      {/* Der frühere Knopf „alle N zeigen" (Grenze 50) entfällt:
          `DataTable` virtualisiert ab 200 Zeilen (Paket U6b). */}
      <DataTable
        name={GATTUNGSNAMEN[gattung] ?? gattung}
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(z) => String(z.id)}
        beiZeileKlick={(z) => setOffen(offen === z.id ? null : z.id)}
        aktiveZeile={offen ?? undefined}
        zeilenKlasse={(z) => (z.istMassgebend ? 'zeile-zugewiesen' : undefined)}
        ausfuhr
        ausfuhrDateiname={`sanitaer-${gattung}`}
        dicht
      />

      {gewaehlt && (
        <ContentSection
          titel={`Rechenweg — ${gewaehlt.bezeichnung}`}
          aktionen={(
            <button className="m-knopf m-knopf-still" onClick={() => setOffen(null)}>
              Schließen
            </button>
          )}
        >
          <Berechnungsblatt zeile={gewaehlt} />
        </ContentSection>
      )}
    </ContentSection>
  );
}

function Berechnungsblatt({ zeile }: { zeile: SanitaerBerechnungszeile }) {
  const werte = Object.entries(zeile.werte ?? {})
    .filter(([, v]) => v !== null && typeof v !== 'object');

  const listen = Object.entries(zeile.werte ?? {})
    .filter(([, v]) => Array.isArray(v));

  // TS9b1a: „Gegeben“ als PropertyGrid statt Kästen mit Farbliteralen, Listen und Teilstrecken als DataTable,
  // der Befund als Marke mit Text (S1.3, S1.5, S1.8).
  return (
    <div className="san-stapel">
      <div>
        <h4>Gegeben</h4>
        <PropertyGrid
          dicht
          spalten={3}
          eintraege={[
            ...werte.map(([k, v]) => ({ beschriftung: lesbar(k), wert: formatiere(v) })),
            ...(zeile.raumnummer ? [{ beschriftung: 'Raum', wert: zeile.raumnummer }] : []),
            ...(zeile.revitElementId !== null ? [{ beschriftung: 'Revit-ElementId', wert: String(zeile.revitElementId) }] : []),
          ]}
        />
      </div>

      {/* Listen — etwa die Küchengegenstände einer Fettabscheiderbemessung.
          Sie passen nicht in eine Zelle und stünden sonst gar nicht da. */}
      {listen.map(([k, v]) => (
        <Werteliste key={k} name={lesbar(k)} zeilen={v as Record<string, unknown>[]} />
      ))}

      {/* ── Die Teilstrecken ────────────────────────────────────────────
          Der Kern eines technischen Nachweises: nicht die Summe, sondern
          wie sie entsteht. Beim Trinkwasserweg 810 Bauteile vom Eintritt
          zur ungünstigsten Zapfstelle, bei einem Netzteil dessen Rohre.

          Die Spalten kommen AUS DEN DATEN — der Trinkwasserweg trägt
          Druckverlust und Geschwindigkeit, das Abwasserrohr Gefälle und
          Füllungsgrad. Eine feste Spaltenliste wäre bei einem der beiden
          leer. */}
      {zeile.teilstrecken && zeile.teilstrecken.length > 0 && (
        <Teilstreckentabelle strecken={zeile.teilstrecken} />
      )}

      {zeile.rechenweg && zeile.rechenweg.length > 0 && (
        <div>
          <h4>Rechenweg</h4>
          <ol className="san-rechenweg">
            {zeile.rechenweg.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}

      {zeile.befund && (
        <p data-befund>
          <StatusBadge zustand={zeile.bewertung === 'beanstandet' ? 'fehler' : 'warnung'}
            text={zeile.bewertung === 'beanstandet' ? 'beanstandet' : 'Hinweis'} klein />{' '}
          <strong>Befund: </strong>{zeile.befund}
        </p>
      )}
    </div>
  );
}

/** Spalten aus den Daten: alle Schlüssel aller Zeilen, die keinen Gegenstand tragen, Zahl als Zahl. */
function datenspalten(zeilen: Record<string, unknown>[]): Spalte<Record<string, unknown>>[] {
  const schluessel = [...new Set(zeilen.flatMap((z) => Object.keys(z)))]
    .filter((k) => zeilen.every((z) => z[k] === null || z[k] === undefined || typeof z[k] !== 'object'));
  return schluessel.map((k) => {
    const zahl = zeilen.every((z) => z[k] === null || z[k] === undefined || typeof z[k] === 'number');
    return {
      schluessel: k, titel: lesbar(k), art: zahl ? 'zahl' as const : 'text' as const,
      wert: (z: Record<string, unknown>) => (z[k] === undefined ? null : z[k]),
      ...(zahl ? {} : { inhalt: (z: Record<string, unknown>) => formatiere(z[k]) }),
    };
  });
}

/** Eine Liste aus den Werten einer Zeile (z. B. die Küchengegenstände eines Fettabscheiders) als `DataTable`. */
function Werteliste({ name, zeilen }: { name: string; zeilen: Record<string, unknown>[] }) {
  const mitNr = zeilen.map((z, i) => ({ ...z, _nr: i }));
  return (
    <DataTable
      name={name}
      spalten={datenspalten(zeilen)}
      zeilen={mitNr}
      schluessel={(z) => String((z as { _nr: number })._nr)}
      dicht
      suche={false}
      spaltenwahl={false}
      leer={<EmptyState klein titel="Die Liste ist leer." />}
    />
  );
}

/**
 * Die Teilstrecken einer Zeile.
 *
 * --- Warum eine eigene Tabelle und keine Liste ---------------------------------
 *
 * 810 Bauteile sind keine Liste. Sie sind eine Tabelle mit einer Zeile je
 * Bauteil und einer Spalte je Größe — so, wie ein liNear-Nachweis sie zeigt.
 *
 * TS9b1a: über `DataTable` statt der rohen Tabelle mit 50er-Seiten - sie rollt ab 200 Zeilen selbst und zeigt alle;
 * die Summen der Lauf-, Verlust- und Mengengrößen stehen im Summenfuß (Fehlstellen werden genannt, nie als 0 gezählt).
 */
function Teilstreckentabelle({ strecken }: {
  strecken: Record<string, unknown>[];
}) {
  // Nur summierbare Größen: Eine Länge summiert sich, eine Nennweite nicht. Erkennbar am Namen - Lauf-, Verlust- und
  // Mengengrößen.
  const spalten = datenspalten(strecken).map((sp) => (sp.art === 'zahl' && /laenge|verlust|druck(?!gefaelle)/i.test(sp.schluessel)
    ? { ...sp, fuss: 'summe' as const } : sp));
  const zeilen = strecken.map((z, i) => ({ ...z, _nr: i }));

  return (
    <DataTable
      name={`Teilstrecken (${formatiereZahl(strecken.length, 0)})`}
      spalten={spalten}
      zeilen={zeilen}
      schluessel={(z) => String((z as { _nr: number })._nr)}
      dicht
      ausfuhr={strecken.length > 20}
      ausfuhrDateiname="sanitaer-teilstrecken-der-zeile"
      leer={<EmptyState klein titel="Keine Teilstrecke an dieser Zeile." />}
    />
  );
}

// --- Kleinteile --------------------------------------------------------------

/**
 * Aus einem Feldnamen eine Überschrift.
 *
 * `summeKaltLs` wird „Summe Kalt Ls", `geschwindigkeit_min` wird
 * „Geschwindigkeit min". Nicht schön, aber lesbar — und eine
 * Übersetzungstabelle für vierzig Feldnamen wäre eine Tabelle, die bei der
 * einundvierzigsten Kennzahl lügt.
 *
 * Der Name darf FEHLEN. Diese Funktion arbeitet auf Daten, deren Form je
 * Bereich eine andere ist — sie bekommt Schlüssel aus JSON, das die Berechnung
 * geschrieben hat, und nicht aus einer festen Schnittstelle.
 *
 * Ohne diese Prüfung ist genau das passiert: Die Annahmen wurden mit großen
 * Anfangsbuchstaben gespeichert und roh durchgereicht, `a.schluessel` war
 * undefined, und die ganze Seite stürzte ab. Der eigentliche Fehler saß im
 * Endpunkt und ist dort behoben — aber eine Anzeigefunktion darf an einem
 * fehlenden Feld nicht die Seite mitnehmen.
 */
function lesbar(name: string | null | undefined): string {
  if (!name) return '—';

  const mit = name.replace(/_/g, ' ');
  const aus = mit.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  return aus.charAt(0).toUpperCase() + aus.slice(1);
}

function formatiere(wert: unknown): string {
  if (wert === null || wert === undefined) return '—';
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein';

  // TS9a: deutsche Schreibweise über das Muster (bis drei Stellen, keine überflüssigen Nullen).
  if (typeof wert === 'number') return formatiereZahl(wert);

  return String(wert);
}
